import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BoardHeader, type BoardLayout } from "@/components/board-header";
import { BoardList } from "@/components/board-list";
import { BoardColumnView } from "@/components/board-column";
import { IntakePanel } from "@/components/intake-panel";
import { WhatsAppPanel } from "@/components/whatsapp-panel";
import { ConnectClaudeDialog } from "@/components/connect-claude-dialog";
import { EditBoardDialog } from "@/components/edit-board-dialog";
import { CreateTaskDialog } from "@/components/create-task-dialog";
import { BoardProjectDialog } from "@/components/board-project-dialog";
import { ColumnDialog } from "@/components/column-dialog";
import { useTouchDrag } from "@/hooks/use-touch-drag";
import { useBoardSort } from "@/hooks/use-board-sort";
import { sortLabel, sortTasks } from "@/lib/task-sort";
import { useCan } from "@/lib/auth";
import { api, ApiError } from "@/lib/api";
import { boardProjectIds, type ProjectOverride } from "@/lib/board-projects";
import { onlyWaiting } from "@/lib/waiting";
import { useWaiting } from "@/lib/waiting-context";
import { qk } from "@/lib/query/keys";
import { useLiveSync } from "@/lib/query/live-sync";
import { applyTaskMove, type BoardsData } from "@/lib/query/optimistic";
import { useElementWidth } from "@/hooks/use-element-width";
import { type BoardColumn, type BoardDetail, type Project, type User } from "@/lib/types";

/** React Flow loads only when a canvas board is opened, so the kanban path never pays for them. */
const CanvasView = lazy(() => import("@/components/canvas-view"));

const LAYOUT_STORAGE_KEY = "automation.boardLayout";
/** A board window this wide or wider shows columns by default; narrower, the stacked list. */
export const BOARD_COLUMNS_MIN_WIDTH = 720;

/**
 * Columns or the stacked list, chosen once per device and shared by every board
 * window, so a choice made in one is the choice in all. Until one is made the
 * layout follows the room the board has (`wide`).
 */
function useChosenLayout(): [BoardLayout | null, (next: BoardLayout) => void] {
  const [chosen, setChosen] = useState<BoardLayout | null>(() => {
    const stored = localStorage.getItem(LAYOUT_STORAGE_KEY);
    return stored === "board" || stored === "list" ? stored : null;
  });
  useEffect(() => {
    const onStorage = (event: Event) => setChosen((event as CustomEvent<BoardLayout>).detail);
    window.addEventListener(LAYOUT_EVENT, onStorage);
    return () => window.removeEventListener(LAYOUT_EVENT, onStorage);
  }, []);
  const choose = useCallback((next: BoardLayout) => {
    localStorage.setItem(LAYOUT_STORAGE_KEY, next);
    window.dispatchEvent(new CustomEvent(LAYOUT_EVENT, { detail: next }));
  }, []);
  return [chosen, choose];
}
const LAYOUT_EVENT = "board:layout";

/** The "drop within a sorted column does nothing" hint, said once per session. */
const SORTED_DROP_HINT_KEY = "automation.boardSort.dropHinted";
function hintSortedDrop(label: string) {
  try {
    if (sessionStorage.getItem(SORTED_DROP_HINT_KEY)) return;
    sessionStorage.setItem(SORTED_DROP_HINT_KEY, "1");
  } catch {
    // No session storage: say it every time rather than never.
  }
  toast(`Sorted by ${label} — switch to Manual order to arrange cards`);
}

/**
 * One board: its header, its columns or list, and every dialog that acts on it.
 * Self-contained so two windows can each hold a board — their drags, filters and
 * dialogs are their own, while the cards come from the one shared boards query, so
 * a move in one shows in the other with no second request.
 */
export function BoardWindow({
  detail,
  allBoards = [],
  users,
  projects,
  now,
  viewportWide,
  windowed,
  titleInTopBar,
  newTaskRequest = 0,
  connectRequest = 0,
  onOpenTask,
  onSetArchived,
  onDelete,
  onManageProjects,
  onChanged,
}: {
  detail: BoardDetail;
  /** Every board, for a canvas's "New card" (which makes the card on a kanban one). */
  allBoards?: BoardDetail[];
  users: User[];
  projects: Project[];
  now: number;
  /** The viewport is laptop-wide: what decides the layout outside a window, as before. */
  viewportWide: boolean;
  /** Inside a window: the layout follows the window's own width instead. */
  windowed: boolean;
  titleInTopBar: boolean;
  /** Bumped by the tab layout's top bar to open the new-task dialog. */
  newTaskRequest?: number;
  /** Bumped by the tab layout's More tray to open Connect Claude Code. */
  connectRequest?: number;
  onOpenTask: (taskId: string) => void;
  onSetArchived: (detail: BoardDetail, archived: boolean) => void;
  onDelete: (detail: BoardDetail) => void;
  onManageProjects: () => void;
  onChanged: () => void;
}) {
  const canEdit = useCan("member");
  const waiting = useWaiting();
  const fail = useCallback((message: string) => toast.error(message), []);

  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskDialogColumn, setTaskDialogColumn] = useState<string | null>(null);
  const [columnDialogOpen, setColumnDialogOpen] = useState(false);
  const [boardProjectOpen, setBoardProjectOpen] = useState(false);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [editBoardOpen, setEditBoardOpen] = useState(false);
  const [editingColumn, setEditingColumn] = useState<BoardColumn | null>(null);
  // Only a bump after mount: the count a window mounts with is an old request.
  const seenTaskRequest = useRef(newTaskRequest);
  useEffect(() => {
    if (newTaskRequest > seenTaskRequest.current) {
      setTaskDialogColumn(null);
      setTaskDialogOpen(true);
    }
    seenTaskRequest.current = newTaskRequest;
  }, [newTaskRequest]);
  const seenConnectRequest = useRef(connectRequest);
  useEffect(() => {
    if (connectRequest > seenConnectRequest.current) setConnectOpen(true);
    seenConnectRequest.current = connectRequest;
  }, [connectRequest]);

  const root = useRef<HTMLDivElement>(null);
  const width = useElementWidth(root);
  const wide = windowed ? (width ?? BOARD_COLUMNS_MIN_WIDTH) >= BOARD_COLUMNS_MIN_WIDTH : viewportWide;
  const [chosenLayout, chooseLayout] = useChosenLayout();
  const layout: BoardLayout = chosenLayout ?? (wide ? "board" : "list");

  const [sort, chooseSort] = useBoardSort(detail.board.id);
  const sorted = sort !== "manual";

  const [draggingTaskId, setDraggingTaskIdState] = useState<string | null>(null);
  // The touch drag reports a target before the render that follows its start.
  const draggingRef = useRef<string | null>(null);
  const setDraggingTaskId = useCallback((taskId: string | null) => {
    draggingRef.current = taskId;
    setDraggingTaskIdState(taskId);
  }, []);
  const [dropTarget, setDropTargetState] = useState<{ columnId: string; index: number } | null>(null);
  const columnOfRef = useRef(new Map<string, string>());
  columnOfRef.current = new Map(detail.tasks.map((task) => [task.id, task.columnId]));
  /**
   * Under a computed sort the slot inside a column means nothing, so the card's own
   * column is no target at all, and another column is a target as a whole.
   */
  const setDropTarget = useCallback(
    (target: { columnId: string; index: number } | null) => {
      const taskId = draggingRef.current;
      setDropTargetState(sorted && target && taskId && columnOfRef.current.get(taskId) === target.columnId ? null : target);
    },
    [sorted],
  );

  /**
   * The "N waiting on you" filter, dropped once nothing is left waiting, so an
   * answered last question does not leave an empty board behind.
   */
  const [waitingOnlyOn, setWaitingOnlyOn] = useState(false);
  const boardWaitingCount = waiting.byBoard.get(detail.board.id) ?? 0;
  const waitingOnly = waitingOnlyOn && boardWaitingCount > 0;
  useEffect(() => {
    if (waitingOnlyOn && waiting.loaded && boardWaitingCount === 0) setWaitingOnlyOn(false);
  }, [waitingOnlyOn, waiting.loaded, boardWaitingCount]);
  const shownTasks = useMemo(
    () => (waitingOnly ? onlyWaiting(detail.tasks, waiting.byTask) : detail.tasks),
    [detail.tasks, waitingOnly, waiting.byTask],
  );

  /**
   * Open requests per card, derived once here rather than fetched per card: the
   * board payload already carries them, and a badge that needs its own request per
   * card is a badge that will not be there.
   */
  const mentionCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const mention of detail.openMentions ?? []) counts.set(mention.taskId, (counts.get(mention.taskId) ?? 0) + 1);
    return counts;
  }, [detail.openMentions]);
  /** Draft replies per card, from the board payload for the same reason. */
  const responseCounts = useMemo(
    () => new Map((detail.responses ?? []).map((entry) => [entry.taskId, entry])),
    [detail.responses],
  );

  /**
   * Only the cards pointed somewhere other than their board's default project: the
   * default is already in the header, so repeating it on every card would bury the
   * handful that differ. On a board covering several projects the chip is what says
   * which repo a card is in; only one outside the board's set is "foreign".
   */
  const memberIds = boardProjectIds(detail).join(",");
  const projectOverrides = useMemo(() => {
    const byId = new Map(projects.map((project) => [project.id, project]));
    const members = memberIds ? memberIds.split(",") : [];
    const overrides = new Map<string, ProjectOverride>();
    for (const task of detail.tasks) {
      if (!task.projectId || task.projectId === detail.board.projectId) continue;
      const project = byId.get(task.projectId);
      if (project) overrides.set(task.id, { project, foreign: !members.includes(project.id) });
    }
    return overrides;
  }, [detail.tasks, detail.board.projectId, memberIds, projects]);

  /**
   * Downloads the board as a spreadsheet: a synthesised anchor rather than
   * `window.open`, which a popup blocker treats as a popup.
   */
  const exportBoard = useCallback(
    (format: "csv" | "xlsx") => {
      const link = document.createElement("a");
      link.href = api.exportUrl(detail.board.id, format);
      link.download = "";
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(format === "xlsx" ? "Excel file downloading" : "CSV downloading", {
        description:
          format === "xlsx"
            ? `${detail.stats.total} card(s), with dropdowns on the state, assignee and priority columns.`
            : `${detail.stats.total} card(s), one row each.`,
      });
    },
    [detail],
  );

  /**
   * A drag lands at once: the card is moved in the cached boards before the request
   * goes, put back if the server refuses, and the views it feeds are refetched once
   * it settles — batched with the revision event its own commit publishes.
   */
  const queryClient = useQueryClient();
  const { localChange } = useLiveSync();
  const move = useMutation({
    mutationFn: ({ taskId, columnId, index }: { taskId: string; columnId: string; index: number }) =>
      // force: dragging is an explicit human decision, so a WIP limit warns rather than blocks.
      api.moveTask(taskId, { column: columnId, index, force: true }),
    onMutate: async ({ taskId, columnId, index }) => {
      await queryClient.cancelQueries({ queryKey: qk.boards() });
      const previous = queryClient.getQueryData<BoardsData>(qk.boards());
      queryClient.setQueryData<BoardsData>(qk.boards(), (data) => applyTaskMove(data, taskId, columnId, index));
      return { previous };
    },
    onError: (cause, _move, context) => {
      if (context?.previous) queryClient.setQueryData(qk.boards(), context.previous);
      fail(cause instanceof ApiError ? cause.message : "Could not move that task");
    },
    onSettled: () => localChange(["boards", "queue", "awaiting", "task", "activity"]),
  });
  const moveCard = useCallback(
    (taskId: string | null, columnId: string, index: number) => {
      setDraggingTaskId(null);
      setDropTargetState(null);
      if (!taskId) return;
      if (sorted) {
        // The order is computed, so a drop only ever changes the card's state, and it
        // goes to the end of the target's manual order.
        if (columnOfRef.current.get(taskId) === columnId) return hintSortedDrop(sortLabel(sort));
        index = detail.tasks.filter((task) => task.columnId === columnId).length;
      }
      move.mutate({ taskId, columnId, index });
    },
    [move.mutate, sorted, sort, detail.tasks, setDraggingTaskId], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const drop = useCallback((columnId: string, index: number) => moveCard(draggingTaskId, columnId, index), [moveCard, draggingTaskId]);
  const clearDrag = () => {
    setDraggingTaskId(null);
    setDropTargetState(null);
  };

  // Fingers: native HTML5 drag never fires for touch, so each board runs its own.
  const boardScroller = useRef<HTMLDivElement>(null);
  const canvas = detail.board.kind === "canvas";
  const [arrangeRequest, setArrangeRequest] = useState(0);
  const [collapseRequest, setCollapseRequest] = useState<{ collapsed: boolean; nonce: number } | null>(null);
  const [exportPngRequest, setExportPngRequest] = useState(0);
  useTouchDrag(boardScroller, layout === "board" && !waitingOnly && !canvas, {
    onStart: setDraggingTaskId,
    onOver: setDropTarget,
    onDrop: (taskId, columnId, index) => void moveCard(taskId, columnId, index),
    onCancel: clearDrag,
  });

  const removeColumn = useCallback(
    async (column: BoardColumn) => {
      const count = detail.tasks.filter((task) => task.columnId === column.id).length;
      const message =
        count > 0 ? `Delete "${column.name}"? Its ${count} task(s) move to the leftmost remaining state.` : `Delete "${column.name}"?`;
      if (!window.confirm(message)) return;
      try {
        const result = await api.deleteColumn(detail.board.id, column.id);
        onChanged();
        toast.success(result.movedTasks > 0 ? `State deleted, ${result.movedTasks} task(s) moved` : "State deleted");
      } catch (cause) {
        fail(cause instanceof ApiError ? cause.message : "Could not delete that state");
      }
    },
    [detail, onChanged, fail],
  );

  const addTask = (columnId: string | null) => {
    setTaskDialogColumn(columnId);
    setTaskDialogOpen(true);
  };
  const addColumn = () => {
    setEditingColumn(null);
    setColumnDialogOpen(true);
  };
  const renameColumn = (column: BoardColumn) => {
    setEditingColumn(column);
    setColumnDialogOpen(true);
  };
  const canAddTasks = canEdit && !detail.board.archived;

  return (
    <div ref={root} className="flex min-h-0 min-w-0 flex-1 flex-col">
      <BoardHeader
        detail={detail}
        now={now}
        onAddTask={() => addTask(null)}
        onAddColumn={addColumn}
        onArchive={() => onSetArchived(detail, !detail.board.archived)}
        onDelete={() => onDelete(detail)}
        onExport={exportBoard}
        onOpenIntake={() => setIntakeOpen(true)}
        onOpenWhatsApp={() => setWhatsappOpen(true)}
        onSetProject={() => setBoardProjectOpen(true)}
        onConnectClaude={() => setConnectOpen(true)}
        onEdit={() => setEditBoardOpen(true)}
        layout={layout}
        onLayoutChange={chooseLayout}
        sort={sort}
        onSortChange={chooseSort}
        titleInTopBar={titleInTopBar}
        waitingCount={boardWaitingCount}
        waitingOnly={waitingOnly}
        onToggleWaiting={() => setWaitingOnlyOn((on) => !on)}
        onArrange={canvas ? () => setArrangeRequest((count) => count + 1) : undefined}
        onCollapseAll={canvas ? (collapsed) => setCollapseRequest({ collapsed, nonce: Date.now() }) : undefined}
        onExportPng={canvas ? () => setExportPngRequest((count) => count + 1) : undefined}
      />

      {canvas ? (
        <Suspense
          fallback={
            <div className="grid min-h-0 flex-1 place-items-center text-muted-foreground">
              <Loader2 className="size-5 animate-spin" />
            </div>
          }
        >
          <CanvasView
            boardId={detail.board.id}
            archived={detail.board.archived}
            users={users}
            boards={allBoards}
            onOpenTask={onOpenTask}
            arrangeRequest={arrangeRequest}
            collapseRequest={collapseRequest}
            exportPngRequest={exportPngRequest}
          />
        </Suspense>
      ) : layout === "list" ? (
        <BoardList
          columns={detail.columns}
          tasks={shownTasks}
          sort={sort}
          boardEndsAt={detail.board.endsAt}
          filtered={waitingOnly}
          users={users}
          now={now}
          mentionCounts={mentionCounts}
          responseCounts={responseCounts}
          projectOverrides={projectOverrides}
          canAddTasks={canAddTasks}
          onOpenTask={onOpenTask}
          onMoveTask={(taskId, columnId, index) =>
            void moveCard(
              taskId,
              columnId,
              // Filtered, the list counts only the cards it shows; append at the real end.
              waitingOnly ? detail.tasks.filter((task) => task.columnId === columnId).length : index,
            )
          }
          onAddTask={addTask}
          onAddColumn={addColumn}
          onRenameColumn={renameColumn}
          onDeleteColumn={(column) => void removeColumn(column)}
        />
      ) : (
        <div
          ref={boardScroller}
          className="grain flex min-h-0 flex-1 snap-x snap-mandatory gap-3 overflow-x-auto overscroll-x-contain scroll-px-4 px-4 py-3 scrollbar-slim win-sm:snap-none win-sm:px-6 win-sm:py-4"
          onDragEnd={clearDrag}
        >
          {detail.columns
            // Under the waiting filter a state with none of those cards is left out.
            .filter((column) => !waitingOnly || shownTasks.some((task) => task.columnId === column.id))
            .map((column) => (
              <BoardColumnView
                key={column.id}
                column={column}
                tasks={sortTasks(
                  shownTasks.filter((task) => task.columnId === column.id),
                  sort,
                  { boardEndsAt: detail.board.endsAt, columnKind: column.kind },
                )}
                dropMarker={!sorted}
                users={users}
                now={now}
                canAddTasks={canAddTasks}
                mentionCounts={mentionCounts}
                responseCounts={responseCounts}
                projectOverrides={projectOverrides}
                draggingTaskId={draggingTaskId}
                dropIndex={dropTarget?.columnId === column.id ? dropTarget.index : null}
                // A drop index among the visible cards only would land in the wrong
                // place in the full column, so the filtered board does not drag.
                onTaskDragStart={waitingOnly ? () => undefined : setDraggingTaskId}
                onTaskDragEnd={clearDrag}
                onDragOverColumn={(columnId, index) => setDropTarget({ columnId, index })}
                onDragLeaveColumn={(columnId) => setDropTargetState((current) => (current?.columnId === columnId ? null : current))}
                onDropOnColumn={(columnId, index) => void drop(columnId, index)}
                onOpenTask={onOpenTask}
                onAddTask={addTask}
                onRenameColumn={renameColumn}
                onDeleteColumn={(column) => void removeColumn(column)}
              />
            ))}

          <button
            onClick={addColumn}
            className="h-fit w-44 shrink-0 rounded-lg border border-dashed border-border/70 px-3 py-2.5 text-left text-[13px] text-muted-foreground transition-colors hover:border-ring/40 hover:bg-muted/40 hover:text-foreground"
          >
            + Add a state
          </button>
        </div>
      )}

      <IntakePanel
        board={detail}
        open={intakeOpen}
        onOpenChange={setIntakeOpen}
        onChanged={onChanged}
        // Leave the chat open behind the card: the reply that named it is the context for reading it.
        onOpenTask={onOpenTask}
        onError={fail}
      />
      <ConnectClaudeDialog board={detail} open={connectOpen} onOpenChange={setConnectOpen} />
      <EditBoardDialog board={detail} projects={projects} open={editBoardOpen} onOpenChange={setEditBoardOpen} onSaved={onChanged} />
      <WhatsAppPanel
        board={detail}
        open={whatsappOpen}
        onOpenChange={setWhatsappOpen}
        onChanged={onChanged}
        onOpenTask={onOpenTask}
        onError={fail}
      />
      <CreateTaskDialog
        board={detail}
        users={users}
        projects={projects}
        defaultColumnId={taskDialogColumn}
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        onCreated={() => {
          onChanged();
          toast.success("Task added");
        }}
      />
      <BoardProjectDialog
        board={detail}
        projects={projects}
        open={boardProjectOpen}
        onOpenChange={setBoardProjectOpen}
        onSaved={() => {
          onChanged();
          toast.success("Board project set");
        }}
        onManageProjects={onManageProjects}
      />
      <ColumnDialog
        board={detail}
        column={editingColumn}
        open={columnDialogOpen}
        onOpenChange={setColumnDialogOpen}
        onSaved={() => {
          onChanged();
          toast.success(editingColumn ? "State updated" : "State added");
        }}
      />
    </div>
  );
}
